import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const s = {
  wrap:   { padding: '28px 32px', maxWidth: 1000 },
  h1:     { fontSize: '1.4rem', color: 'var(--admin-text)', fontFamily: 'Inter,sans-serif', fontWeight: 600, marginBottom: 6 },
  sub:    { fontSize: '.78rem', color: 'var(--admin-text-dim)', marginBottom: 28 },
  secLbl: { fontSize: '.7rem', fontWeight: 600, color: 'var(--admin-text-dim)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12, display: 'block' },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: '.78rem', background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 8, overflow: 'hidden' },
  th:     { padding: '10px 16px', textAlign: 'left', color: 'var(--admin-text-dim)', fontWeight: 600, background: 'var(--admin-bg-dark)', borderBottom: '1px solid var(--admin-border)' },
  tdFn:   (i) => ({ padding: '10px 16px', borderBottom: '1px solid var(--admin-border)', background: i % 2 === 0 ? 'var(--admin-bg)' : 'var(--admin-bg-alt)', verticalAlign: 'middle' }),
  select: { background: 'var(--admin-bg-dark)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', borderRadius: 4, padding: '4px 8px', fontSize: '.73rem', cursor: 'pointer' },
  errBox: { background: 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 6, padding: '10px 14px', color: 'var(--admin-danger)', fontSize: '.78rem', marginBottom: 20 },
};

export default function Packages() {
  const [users, setUsers]       = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [ud, pd] = await Promise.all([api.getUsers(), api.getPackages()]);
      setUsers(ud.users || []);
      setPackages(pd.packages || []);
    } catch (e) {
      setError('Fehler beim Laden: ' + (e.message || 'Unbekannt'));
    } finally {
      setLoading(false);
    }
  }

  async function handlePackageChange(userId, packageId) {
    setSaving(userId);
    try {
      await api.setUserPackage(userId, parseInt(packageId));
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, package_id: parseInt(packageId) } : u));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  }

  const pkgLabel = (id) => packages.find(p => p.id === id)?.label || '—';

  if (loading) return <div style={{ padding: 32, color: 'var(--admin-text-dim)' }}>Lade...</div>;

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>Pakete & Zuweisungen</h1>
      <p style={s.sub}>Übersicht der verfügbaren Pakete und Benutzer-Zuweisung.</p>

      {error && (
        <div style={s.errBox}>
          {error}
          <button onClick={load} style={{ marginLeft: 12, background: 'none', border: '1px solid var(--admin-danger)', color: 'var(--admin-danger)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: '.72rem' }}>
            Neu laden
          </button>
        </div>
      )}

      {/* Paket-Karten */}
      <div style={{ marginBottom: 40 }}>
        <span style={s.secLbl}>Verfügbare Pakete</span>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {packages.map(pkg => (
            <div key={pkg.id} style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 8, padding: '16px 20px', minWidth: 190, flex: '1 1 190px', maxWidth: 260 }}>
              <div style={{ color: 'var(--admin-text)', fontWeight: 700, fontSize: '.9rem', marginBottom: 4 }}>{pkg.label}</div>
              <div style={{ color: 'var(--admin-text-dim)', fontSize: '.72rem', marginBottom: 8 }}>
                {pkg.price_monthly === 0 ? 'Kostenlos' : `${pkg.price_monthly.toFixed(2)} €/Monat`}
                {' · '}Scans: {pkg.scan_limit === 0 ? '∞' : `${pkg.scan_limit}/Monat`}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {pkg.modules.length === 0
                  ? <span style={{ color: 'var(--admin-text-dim)', fontSize: '.68rem' }}>Keine Module</span>
                  : pkg.modules.map(m => (
                    <span key={m} style={{ background: 'var(--admin-bg-analysis)', color: 'var(--admin-field-name)', borderRadius: 3, padding: '1px 6px', fontSize: '.62rem' }}>{m}</span>
                  ))
                }
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Benutzer-Tabelle */}
      <div>
        <span style={s.secLbl}>Benutzer-Pakete ({users.length})</span>
        <table style={s.table}>
          <thead>
            <tr>
              {['Benutzer', 'E-Mail', 'Rolle', 'Aktuelles Paket', 'Ändern'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.id}>
                <td style={{ ...s.tdFn(i), color: 'var(--admin-text)', fontWeight: 600 }}>{u.username}</td>
                <td style={{ ...s.tdFn(i), color: 'var(--admin-text-muted)' }}>{u.email || '—'}</td>
                <td style={s.tdFn(i)}>
                  {u.is_admin
                    ? <span style={{ color: 'var(--admin-accent)', fontSize: '.7rem', fontWeight: 600 }}>⚙ Admin</span>
                    : <span style={{ color: 'var(--admin-text-dim)', fontSize: '.7rem' }}>Benutzer</span>}
                </td>
                <td style={{ ...s.tdFn(i), color: 'var(--admin-link)', fontSize: '.75rem' }}>
                  {pkgLabel(u.package_id)}
                </td>
                <td style={s.tdFn(i)}>
                  <select style={s.select} value={u.package_id || 1}
                    onChange={e => handlePackageChange(u.id, e.target.value)}
                    disabled={saving === u.id}
                  >
                    {packages.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                  {saving === u.id && <span style={{ color: 'var(--admin-text-dim)', fontSize: '.68rem', marginLeft: 8 }}>...</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
